import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import api from '../../../domain/api';

const fetchGoogleAdsCustomers = async () => {
  const res = await api.get('/google-ads/customers');
  return res.data;
};

const GoogleAdsCustomersReport = () => {
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const history = useHistory();

  // Handle customer name click to navigate to detailed campaigns
  const handleCustomerClick = (customerId, customerName) => {
    // Stay on same route but switch to campaigns tab with customer selected
    const params = new URLSearchParams(window.location.search);
    params.set('tab', 'campaigns');
    params.set('customerId', customerId);
    params.set('dateFilter', 'last_30_days');
    params.set('sortBy', 'cost');
    params.set('sortOrder', 'desc');
    params.set('page', '1');
    params.set('limit', '15');

    history.push({
      pathname: window.location.pathname,
      search: params.toString(),
    });
  };

  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['google-ads-customers'],
    queryFn: fetchGoogleAdsCustomers,
    retry: false, // Disable retry attempts
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: false, // Disable refetch on component mount after initial load
    refetchOnReconnect: false, // Disable refetch on network reconnect
    staleTime: Infinity, // Data never becomes stale automatically
  });

  // Filter customers based on selected filters
  const filteredCustomers = React.useMemo(() => {
    if (!response?.customers) return [];

    let filtered = response.customers;

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(
        (customer) =>
          customer.status?.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Filter by type (manager vs client)
    if (selectedType !== 'all') {
      if (selectedType === 'managers') {
        filtered = filtered.filter((customer) => customer.isManager);
      } else if (selectedType === 'clients') {
        filtered = filtered.filter((customer) => !customer.isManager);
      }
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (customer) =>
          customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.id?.includes(searchTerm) ||
          customer.formattedId?.includes(searchTerm)
      );
    }

    return filtered;
  }, [response?.customers, selectedStatus, selectedType, searchTerm]);

  // Calculate statistics from customers data if not provided by API
  const statistics = React.useMemo(() => {
    if (!response?.customers)
      return {
        total: 0,
        byStatus: {},
        byType: { managers: 0, clients: 0 },
      };

    const customers = response.customers;
    const total = customers.length;

    // Count by status
    const byStatus = customers.reduce((acc, customer) => {
      const status = customer.status?.toLowerCase() || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Count by type
    const managers = customers.filter((c) => c.isManager).length;
    const clients = customers.filter((c) => !c.isManager).length;

    return {
      total,
      byStatus,
      byType: {
        managers,
        clients,
      },
    };
  }, [response?.customers]);

  if (isLoading) {
    return (
      <div className='loading-container'>
        <div className='loading-spinner'></div>
        <p>Loading Google Ads customers...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='error-container'>
        <i className='fa fa-exclamation-triangle'></i>
        <p>{error?.message || 'Failed to fetch Google Ads customers'}</p>
      </div>
    );
  }

  return (
    <div className='google-ads-customers-container'>
      <div className='google-ads-header'>
        <div className='header-left'>
          <div className='header-title-section'>
            <h1 className='header-title'>
              <i className='fab fa-google'></i>
              <div className='title-content'>
                <span className='title-text'>Google Ads Customers Report</span>
                <span className='header-subtitle'>
                  Comprehensive customer analytics dashboard
                </span>
              </div>
            </h1>
          </div>
          <div className='stats-summary'>
            <span className='stat-item'>
              Total: <strong>{statistics.total || 0}</strong>
            </span>
            <span className='stat-item'>
              Enabled: <strong>{statistics.byStatus?.enabled || 0}</strong>
            </span>
            <span className='stat-item'>
              Canceled: <strong>{statistics.byStatus?.canceled || 0}</strong>
            </span>
            <span className='stat-item'>
              Managers: <strong>{statistics.byType?.managers || 0}</strong>
            </span>
            <span className='stat-item'>
              Clients: <strong>{statistics.byType?.clients || 0}</strong>
            </span>
          </div>
        </div>
        <div className='header-actions'>
          <button
            className='refresh-button'
            onClick={() => refetch()}
            disabled={isLoading}
            title='Refresh customer data'
          >
            <i className={`fa fa-refresh ${isLoading ? 'fa-spin' : ''}`}></i>
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className='filters-container'>
        <div className='filter-group'>
          <label>Search:</label>
          <input
            type='text'
            placeholder='Search by name, ID...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='search-input'
          />
        </div>

        <div className='filter-group'>
          <label>Status:</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className='filter-select'
          >
            <option value='all'>All Status</option>
            <option value='enabled'>Enabled</option>
            <option value='paused'>Paused</option>
            <option value='suspended'>Suspended</option>
            <option value='canceled'>Canceled</option>
            <option value='unknown'>Unknown</option>
          </select>
        </div>

        <div className='filter-group'>
          <label>Type:</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className='filter-select'
          >
            <option value='all'>All Types</option>
            <option value='managers'>Managers</option>
            <option value='clients'>Clients</option>
          </select>
        </div>

        <div className='filter-stats'>
          Showing <strong>{filteredCustomers.length}</strong> of{' '}
          <strong>{response?.customers?.length || 0}</strong> customers
        </div>
      </div>

      {/* Table */}
      <div className='table-container'>
        {filteredCustomers.length === 0 ? (
          <div className='no-data-container'>
            <i
              className='fa fa-search'
              style={{ fontSize: 24, marginBottom: 12, opacity: 0.5 }}
            ></i>
            <p>No customers found matching your criteria</p>
          </div>
        ) : (
          <table className='customers-table'>
            <thead className='table-header'>
              <tr>
                <th>Customer Name</th>
                <th>Associated Client</th>
                <th>Customer ID</th>
                <th>Status</th>
                <th>Type</th>
                <th>Currency</th>
                <th>Time Zone</th>
                <th>Level</th>
              </tr>
            </thead>
            <tbody className='table-body'>
              {filteredCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td>
                    <button
                      className='customer-name-button'
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px 0',
                        margin: 0,
                        color: '#4285f4',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontWeight: 600,
                        fontSize: 'inherit',
                        textAlign: 'left',
                        width: '100%',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease',
                      }}
                      title='Click to view customer campaigns'
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.color = '#1a73e8';
                        e.target.style.textDecoration = 'none';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#4285f4';
                        e.target.style.textDecoration = 'underline';
                      }}
                      onClick={() =>
                        handleCustomerClick(customer.id, customer.name)
                      }
                    >
                      {customer.name}
                      {customer.isTestAccount && (
                        <span
                          className='test-badge'
                          style={{
                            marginLeft: '8px',
                            fontSize: '10px',
                            background: '#fff3cd',
                            color: '#856404',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontWeight: 'bold',
                            textDecoration: 'none',
                          }}
                        >
                          TEST
                        </span>
                      )}
                    </button>
                  </td>
                  <td>
                    {customer.clientAssociated &&
                    customer.clientAssociated !== 'N/A' ? (
                      <button
                        className='client-name-button'
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '4px 0',
                          margin: 0,
                          color: '#7c3aed',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontWeight: 600,
                          fontSize: 'inherit',
                          textAlign: 'left',
                          width: '100%',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                        }}
                        title='Click to view customer campaigns'
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.color = '#6d28d9';
                          e.target.style.textDecoration = 'none';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#7c3aed';
                          e.target.style.textDecoration = 'underline';
                        }}
                        onClick={() =>
                          handleCustomerClick(customer.id, customer.name)
                        }
                      >
                        {customer.clientAssociated}
                      </button>
                    ) : (
                      <span className='customer-id'>N/A</span>
                    )}
                  </td>

                  <td>
                    <span className='customer-id'>
                      {customer.formattedId || customer.id}
                    </span>
                  </td>

                  <td>
                    <span
                      className={`status-badge status-${customer.status?.toLowerCase()}`}
                    >
                      <i
                        className='fa fa-circle'
                        style={{
                          color: getStatusColor(customer.status),
                          marginRight: '6px',
                        }}
                      ></i>
                      {customer.status}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`type-badge ${
                        customer.isManager ? 'manager' : 'client'
                      }`}
                    >
                      <i
                        className={`fa ${
                          customer.isManager ? 'fa-user-tie' : 'fa-user'
                        }`}
                        style={{ marginRight: '6px' }}
                      ></i>
                      {customer.isManager ? 'Manager' : 'Client'}
                    </span>
                  </td>
                  <td>{customer.currencyCode}</td>
                  <td>{customer.timeZone}</td>
                  <td>
                    <span className='level-badge'>Level {customer.level}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <style jsx>{`
        .google-ads-customers-container {
          background: #f8f9fa;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            Helvetica, Arial, sans-serif;
        }

        .google-ads-header {
          background: linear-gradient(
            135deg,
            #4285f4 0%,
            #1a73e8 50%,
            #3367d6 100%
          );
          color: white;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 4px 16px rgba(66, 133, 244, 0.2);
          border-bottom: 2px solid #3367d6;
          position: relative;
          overflow: hidden;
          margin-bottom: 20px;
        }

        .google-ads-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          animation: shimmer 3s infinite;
        }

        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        .header-left {
          display: flex;
          flex-direction: column;
          gap: 16px;
          flex: 1;
        }

        .header-title-section {
          display: flex;
          align-items: center;
        }

        .header-title {
          font-size: 20px;
          font-weight: 700;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          letter-spacing: 0.3px;
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
          z-index: 1;
        }

        .title-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .header-title i {
          font-size: 18px;
          opacity: 0.95;
          flex-shrink: 0;
        }

        .title-text {
          font-weight: 700;
          color: white;
          white-space: nowrap;
        }

        .header-subtitle {
          font-size: 11px;
          opacity: 0.75;
          font-weight: 400;
          font-style: italic;
          color: rgba(255, 255, 255, 0.8);
          white-space: nowrap;
        }

        .header-actions {
          display: flex;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .refresh-button {
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          border-radius: 6px;
          padding: 8px 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
          backdrop-filter: blur(10px);
          font-size: 14px;
          font-weight: 500;
        }

        .refresh-button:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .refresh-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .stats-summary {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          position: relative;
          z-index: 1;
        }

        .stat-item {
          background: rgba(255, 255, 255, 0.15);
          padding: 8px 12px;
          border-radius: 10px;
          font-size: 13px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .stat-item:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-1px);
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
        }

        .filters-container {
          background: white;
          padding: 20px;
          margin-bottom: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          display: flex;
          gap: 20px;
          align-items: end;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .filter-group label {
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .search-input,
        .filter-select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          min-width: 150px;
        }

        .search-input {
          min-width: 250px;
        }

        .filter-stats {
          margin-left: auto;
          color: #6b7280;
          font-size: 14px;
        }

        .table-container {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .customers-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .table-header th {
          background: #f8f9fa;
          padding: 16px 12px;
          text-align: left;
          border-bottom: 2px solid #e5e7eb;
          font-weight: 600;
          color: #374151;
        }

        .table-body td {
          padding: 12px;
          border-bottom: 1px solid #f1f3f4;
        }

        .table-body tr:hover {
          background-color: #f8f9fa;
        }

        .customer-name {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
        }

        .customer-name.clickable {
          color: #4285f4;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 4px 8px;
          border-radius: 6px;
          margin: -4px -8px;
        }

        .customer-name.clickable:hover {
          background-color: rgba(66, 133, 244, 0.1);
          color: #3367d6;
          text-decoration: underline;
        }

        .test-badge {
          background: #fbbf24;
          color: #92400e;
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 600;
        }

        .customer-id {
          font-family: 'Courier New', monospace;
          background: #f3f4f6;
          padding: 4px 8px;
          border-radius: 4px;
          color: #374151;
        }

        .status-badge {
          display: flex;
          align-items: center;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-enabled {
          background: #d1fae5;
          color: #065f46;
        }

        .status-paused {
          background: #fef3c7;
          color: #92400e;
        }

        .status-suspended {
          background: #fde68a;
          color: #b45309;
        }

        .status-canceled {
          background: #fecaca;
          color: #991b1b;
        }

        .status-unknown {
          background: #e5e7eb;
          color: #374151;
        }

        .type-badge {
          display: flex;
          align-items: center;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        .type-badge.manager {
          background: #dbeafe;
          color: #1e40af;
        }

        .type-badge.client {
          background: #f3e8ff;
          color: #7c3aed;
        }

        .level-badge {
          background: #f1f5f9;
          color: #475569;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        .loading-container,
        .error-container,
        .no-data-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #6b7280;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #4285f4;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .error-container i {
          font-size: 48px;
          color: #ef4444;
          margin-bottom: 16px;
        }
      `}</style>
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'enabled':
      return '#10b981';
    case 'paused':
      return '#f59e0b';
    case 'suspended':
      return '#f97316';
    case 'canceled':
      return '#ef4444';
    default:
      return '#6b7280';
  }
};

export default GoogleAdsCustomersReport;
